import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  ExternalLink,
  Package,
  Euro,
  ArrowLeft,
  Link2,
  Edit,
  Plus,
} from "lucide-react";
import { Card } from "../../../components/cards";
import { formatNumber } from "../../../helpers/string";
import { useReference, useLinkProduct } from "../hooks";
import { Loading } from "../../../components";
import { ProductInput } from "../../products/components/product-input";
import { useMe } from "../../users/hooks/useMe";
import { UserRole } from "../../../__generated__/globalTypes";
import {
  LinkedProductsPriceGraph,
  LinkedProductsStockGraph,
} from "../components";

type ReferenceParams = {
  id: string;
};

export const Reference: React.FC = () => {
  const { id } = useParams<ReferenceParams>();
  const navigate = useNavigate();
  const { data: userData } = useMe();

  useEffect(() => {
    if (!id) {
      navigate("/references");
    }
  }, []);

  const { data, loading } = useReference(+id!);
  const [linkProduct, { loading: linkingProduct }] = useLinkProduct();

  const form = useForm({
    defaultValues: {
      productId: null,
    },
  });

  const handleLinkProduct = async () => {
    const productId = form.getValues("productId");
    if (!productId) {
      toast.error("Veuillez sélectionner un produit");
      return;
    }

    try {
      const result = await linkProduct({
        variables: {
          referenceId: +id!,
          productId: +productId,
        },
      });

      if (result.data?.linkProductToReference?.ok) {
        toast.success("Produit lié avec succès");
        form.reset({ productId: null });
      } else {
        toast.error(
          result.data?.linkProductToReference?.error ||
            "Erreur lors de la liaison"
        );
      }
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de la liaison");
    }
  };

  if (loading) return <Loading />;

  const reference = data?.reference?.result;
  const isAdmin = userData?.me?.role === UserRole.Admin;

  console.log("reference", data);

  return (
    <>
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200 mb-2">
        <div className="main-container pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/references")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {reference?.name ?? "Chargement..."}
                </h1>
                {reference?.reference && (
                  <p className="text-sm text-gray-500 mt-1">
                    Réf: {reference.reference}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => navigate(`/reference/${id}/update`)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Edit size={16} />
              Modifier
            </button>
          </div>
        </div>
      </div>

      <div className="main-container px-4">
        <div className="section">
          <div className="w-full md:flex md:gap-2 mb-2">
            {/* Left: Reference Info (2/3) */}
            <div className="w-full md:w-1/2 mb-2">
              <Card padding="md" className="shadow-sm border border-gray-200">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-gray-900">
                    Informations de la référence
                  </h3>
                </div>

                <div className="space-y-4">
                  {/* Image */}
                  {reference?.imgUrl && (
                    <div className="flex justify-center">
                      <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        <img
                          src={reference.imgUrl}
                          alt={reference.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  )}

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Prix */}
                    <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Euro size={18} className="text-emerald-600" />
                        <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider">
                          Prix
                        </p>
                      </div>
                      <p className="text-2xl font-bold text-emerald-900">
                        {formatNumber(reference?.lastPrice ?? 0)} €
                      </p>
                    </div>

                    {/* Quantité */}
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Package size={18} className="text-blue-600" />
                        <p className="text-xs font-medium text-blue-700 uppercase tracking-wider">
                          Quantité
                        </p>
                      </div>
                      <p className="text-2xl font-bold text-blue-900">
                        {formatNumber(reference?.lastQuantity ?? 0)} pcs
                      </p>
                    </div>
                  </div>

                  {/* URL */}
                  {reference?.url && (
                    <div className="overflow-hidden pt-4 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                        URL du produit
                      </p>
                      <a
                        href={reference.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        <ExternalLink size={16} />
                        {reference.url}
                      </a>
                    </div>
                  )}

                  {/* User Info - Admin Only */}
                  {isAdmin && reference?.user && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                        Créé par
                      </p>
                      <p className="text-sm text-gray-700">
                        {reference.user.firstname} {reference.user.lastname}
                        <span className="text-gray-500 ml-2">
                          ({reference.user.email})
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Right: Linked Products (1/3) */}
            <div className="w-full md:w-1/2">
              <Card padding="md" className="shadow-sm border border-gray-200">
                {/* Add Product Form - Admin Only */}
                {isAdmin && (
                  <div className="pb-4 border-b mb-2 border-gray-200">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                      Ajouter un produit
                    </p>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <ProductInput form={form} />
                      </div>
                      <button
                        onClick={handleLinkProduct}
                        disabled={linkingProduct}
                        className="self-end mb-1 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 rounded-lg transition-colors"
                      >
                        {linkingProduct ? (
                          "Liaison..."
                        ) : (
                          <>
                            <Plus size={16} />
                            Lier
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Link2 size={18} className="text-gray-600" />
                    <h3 className="text-base font-semibold text-gray-900">
                      Produits liés
                    </h3>
                  </div>
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {reference?.products?.length || 0}
                  </span>
                </div>

                {reference?.products && reference.products.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {reference.products.map((rp) => (
                      <div
                        key={rp.id}
                        className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer"
                        onClick={() => navigate(`/product/${rp.product?.id}`)}
                      >
                        <div className="flex items-start gap-2">
                          {rp.product?.imgUrl ? (
                            <img
                              src={rp.product.imgUrl}
                              alt={rp.product.name}
                              className="w-12 h-12 rounded object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                              <Package size={20} className="text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {rp.product?.name}
                            </p>
                            {rp.product?.site && (
                              <p className="text-xs text-gray-500 mt-1">
                                {rp.product.site.name}
                              </p>
                            )}
                            {rp.product?.lastPrice && (
                              <p className="text-xs font-semibold text-emerald-600 mt-1">
                                {formatNumber(rp.product.lastPrice)} €
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 mb-4">
                    <Link2 size={32} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      Aucun produit lié pour le moment
                    </p>
                  </div>
                )}
              </Card>
            </div>
          </div>

          {/* Graphs Section */}
          {reference?.products && reference.products.length > 0 && (
            <div className="w-full md:flex md:gap-2">
              {/* Price Evolution Graph */}
              <div className="w-full md:w-1/2 mb-2">
                <Card padding="md" className="shadow-sm border border-gray-200">
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-gray-900">
                      Évolution des prix des produits liés
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Comparaison des prix dans le temps
                    </p>
                  </div>
                  <LinkedProductsPriceGraph products={reference.products} />
                </Card>
              </div>

              {/* Stock Evolution Graph */}
              <div className="w-full md:w-1/2">
                <Card padding="md" className="shadow-sm border border-gray-200">
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-gray-900">
                      Évolution du stock des produits liés
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Comparaison des quantités dans le temps
                    </p>
                  </div>
                  <LinkedProductsStockGraph products={reference.products} />
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
